/**
 * Host resolver for Phase 2 (Public Browsing on Custom Academy Domains).
 *
 * This module decides — purely from `window.location.hostname` — whether
 * the current request is being served on a Dwrrah platform host or on an
 * instructor-owned custom academy domain.
 *
 * It is intentionally:
 *   - Read-only against `window.location` (no navigation, no auth side effects).
 *   - Decoupled from `useStudentAuth.js` and any slug-based routing.
 *   - Safe in SSR / non-browser contexts (returns "platform").
 *
 * Reserved platform hosts (NEVER treated as a custom academy host):
 *   - dwrrah.com / www.dwrrah.com
 *   - app.dwrrah.com / admin.dwrrah.com / meet.dwrrah.com
 *   - *.lovable.app / *.lovable.dev / *.lovableproject.com
 *   - dwrrah-platform.pages.dev (Cloudflare Pages canonical project host)
 *   - *.dwrrah-platform.pages.dev (Cloudflare Pages preview deployments
 *     for the platform project, e.g. 68692466.dwrrah-platform.pages.dev).
 *     Matched by exact suffix `.dwrrah-platform.pages.dev` (with leading
 *     dot) so unrelated `pages.dev` hosts and look-alikes such as
 *     `evil-dwrrah-platform.pages.dev` are NOT trusted.
 *   - localhost / 127.0.0.1 (dev)
 */

const PLATFORM_EXACT = new Set([
  "dwrrah.com",
  "www.dwrrah.com",
  "app.dwrrah.com",
  "admin.dwrrah.com",
  "meet.dwrrah.com",
  "dwrrah-platform.pages.dev",
  "localhost",
  "127.0.0.1",
]);

const PLATFORM_SUFFIXES = [
  ".lovable.app",
  ".lovable.dev",
  ".lovableproject.com",
  // Cloudflare Pages preview deployments for the Dwrrah platform project.
  // The leading dot guarantees we only match direct subdomains of the
  // canonical project host (e.g. `<hash>.dwrrah-platform.pages.dev`) and
  // never any unrelated `*.pages.dev` host or look-alike domain.
  ".dwrrah-platform.pages.dev",
];

// The canonical main domain used to build redirect URLs for protected paths.
export const MAIN_DOMAIN_ORIGIN = "https://dwrrah.com";

export function getCurrentHost() {
  if (typeof window === "undefined" || !window.location) return "";
  return String(window.location.hostname || "").toLowerCase();
}

/**
 * Returns true when the host is a Dwrrah platform host (not a custom domain).
 */
export function isPlatformHost(hostInput) {
  const host = String(hostInput ?? getCurrentHost() ?? "").toLowerCase().trim();
  if (!host) return true; // SSR / unknown → platform
  if (PLATFORM_EXACT.has(host)) return true;
  if (PLATFORM_SUFFIXES.some((suffix) => host.endsWith(suffix))) return true;
  return false;
}

/**
 * Returns true when the host is a custom academy domain (not platform).
 * Note: this does NOT verify the domain is mapped to a real academy. That
 * verification happens in `useHostAcademy` against `academies_public`.
 */
export function isCustomAcademyHost(hostInput) {
  return !isPlatformHost(hostInput);
}

/**
 * Joins URL pieces without ever producing a `//` after the origin (other
 * than the protocol's own `://`). Trims slashes from `path` and re-prefixes
 * exactly one slash.
 */
export function joinUrl(origin, path) {
  const cleanOrigin = String(origin || "").replace(/\/+$/, "");
  const rawPath = String(path || "");
  const cleanPath = rawPath.replace(/^\/+/, "");
  return cleanPath ? `${cleanOrigin}/${cleanPath}` : cleanOrigin;
}

/**
 * Build a safe redirect URL back to the main Dwrrah domain.
 *
 * Usage examples:
 *   buildMainDomainRedirect({ targetType: "student", academySlug: "demo", path: "dashboard" })
 *     → https://dwrrah.com/demo/dashboard
 *
 *   buildMainDomainRedirect({ targetType: "instructor", path: "dashboard" })
 *     → https://dwrrah.com/instructor/dashboard
 *
 *   buildMainDomainRedirect({ targetType: "admin", path: "dashboard" })
 *     → https://dwrrah.com/admin/dashboard
 *
 * Always appends `?return_to=<currentUrl>` when `returnTo` is provided so the
 * main domain can route the user back after login/checkout. NEVER produces
 * a URL containing `//` after the origin.
 */
export function buildMainDomainRedirect({
  targetType = "student",
  academySlug = "",
  path = "",
  returnTo = "",
  search = "",
  fallbackReason = "",
} = {}) {
  const slug = String(academySlug || "").trim().replace(/^\/+|\/+$/g, "");
  const cleanPath = String(path || "").replace(/^\/+|\/+$/g, "");

  let pathSegment;
  if (targetType === "instructor") {
    pathSegment = cleanPath ? `instructor/${cleanPath}` : "instructor";
  } else if (targetType === "admin") {
    pathSegment = cleanPath ? `admin/${cleanPath}` : "admin";
  } else if (targetType === "auth") {
    // /login, /signup, /reset-password, /post-login on the main domain
    pathSegment = cleanPath;
  } else {
    // student
    if (slug && cleanPath) pathSegment = `${slug}/${cleanPath}`;
    else if (slug) pathSegment = slug;
    else pathSegment = cleanPath;
  }

  const url = joinUrl(MAIN_DOMAIN_ORIGIN, pathSegment);

  // Build query string safely (never starts with `?` from caller).
  const params = new URLSearchParams();
  if (search) {
    const s = String(search).replace(/^\?/, "");
    if (s) {
      const sp = new URLSearchParams(s);
      sp.forEach((v, k) => params.set(k, v));
    }
  }
  if (returnTo) params.set("return_to", String(returnTo));

  const qs = params.toString();
  // Append fallback_reason last so it always appears (not overridden by
  // upstream search). Used by Phase 23.1 to surface a one-time toast on
  // the platform slug after a soft-suspended custom-domain redirect.
  if (fallbackReason) {
    const sep = qs ? "&" : "?";
    const final = `${url}${qs ? `?${qs}` : ""}${sep}fallback_reason=${encodeURIComponent(String(fallbackReason))}`;
    return final;
  }
  return qs ? `${url}?${qs}` : url;
}

/**
 * Returns the current full URL (used as `return_to`). Safe in SSR.
 */
export function getCurrentReturnTo() {
  if (typeof window === "undefined" || !window.location) return "";
  return window.location.href;
}

export const __platformHostsForTest = {
  PLATFORM_EXACT: Array.from(PLATFORM_EXACT),
  PLATFORM_SUFFIXES: [...PLATFORM_SUFFIXES],
};